import { Trans } from '@lingui/react';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { Box } from '@startuptools/ui/Box';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { useWpaSetStrikePriceMutation } from '../../../../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Button } from '@startuptools/ui/Button';
import { ErrorDisplay } from '../../../../../components/error-display/ErrorDisplay';
import { GqlWarrantProgram } from '../../../../../graphql/base-types.graphql';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../../components/steppers/common/StepperStepContentContext';
import { WarrantProgramDocument } from '../../../WarrantProgram.graphql';

type FormValues = {
  strikePrice: string;
};

export const SnrBasedStepStrikePrice = ({
  warrantProgram,
}: {
  warrantProgram: Pick<GqlWarrantProgram, 'id' | 'strikePrice' | 'currency'>;
}) => {
  const companyId = useCompanyId();
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();
  // eslint-disable-next-line sut/loading-mutation
  const [doSetStrikePrice] = useWpaSetStrikePriceMutation({ refetchQueries: [WarrantProgramDocument] });

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: { strikePrice: warrantProgram.strikePrice ?? '' },
  });

  const onSubmit: SubmitHandler<FormValues> = async values => {
    await doSetStrikePrice({
      variables: {
        companyId,
        input: {
          id: warrantProgram.id,
          strikePrice: values.strikePrice!.toString(),
          useNetStrike: false,
        },
      },
    });
    return stepNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="What is the strike price for the option?" />
        </Typography>

        <Box maxWidth={300}>
          <Controller
            control={control}
            name="strikePrice"
            render={({ field }) => (
              <FormControl>
                <FormLabel>
                  <Trans id="Strike Price" />
                </FormLabel>
                <NumericFormatInput {...field} endDecorator={<span>{warrantProgram.currency}</span>} />
              </FormControl>
            )}
          />
        </Box>

        {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

        <Box>
          <Button type="submit">
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
